import { Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { EncrDecrService } from '../../../_services/encode.decode.service';
import { NotificationService } from '../../../_services/notification.service';
import { ChecksService } from '../../../_services/checks.service';
import { UserData } from '../../../_models/users';
import { ApiService } from '../../../_services/api.service';
import { Router } from '@angular/router';
import { AdminData } from '../../../_models/admin';
import { HrAnnouncementData } from '../../../_models/hr';

interface TickerItem {
  pair: string;
  value: string;
  change: string;
  down: boolean;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {
  userData: AdminData;
  auth: number;
  notificationCount: number = 0;
  unreadAnnouncementCount: number = 0;
  unreadAnnouncements: HrAnnouncementData[] = [];
  tickerItems: TickerItem[] = [];
  tickerSourceText: string = 'Loading rates';
  private tickerRefresh: any;

  pageUrl = this.router.url.slice(1);

  constructor(
    public checkService: ChecksService,
    private apiService: ApiService,
    private notifyService: NotificationService,
    private router: Router,
    private EncrDecr: EncrDecrService
  ) { }

  ngOnInit(): void {
    this.userData = this.checkService.checkSession(true);
    this.auth = parseInt(localStorage.getItem('auth'));
    this.loadAnnouncementNotifications();
    this.loadTickerData();
    this.tickerRefresh = setInterval(() => this.loadTickerData(), 60000);
  }

  ngOnDestroy(): void {
    if (this.tickerRefresh) {
      clearInterval(this.tickerRefresh);
    }
  }

  loadAnnouncementNotifications(): void {
    if (!this.userData?.token?.token) {
      return;
    }

    this.apiService.getHrAnnouncementNotifications().subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.unreadAnnouncementCount = data.data?.count || 0;
        this.unreadAnnouncements = data.data?.announcements || [];
      }
    });
  }

  markAnnouncementRead(announcement?: HrAnnouncementData): void {
    this.apiService.markHrAnnouncementRead(announcement?.ref || 0).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.unreadAnnouncementCount = data.data?.count || 0;
        this.unreadAnnouncements = data.data?.announcements || [];
      }
    });
  }

  loadTickerData(): void {
    if (!this.userData?.token?.token) {
      return;
    }

    this.apiService.getTreasuryData().subscribe(data => {
      this.checkService.checkLoggedin(data);
      const rates = data.success === true ? this.treasuryTickerItems(data.data?.rates || []) : [];
      if (rates.length > 0) {
        this.tickerItems = rates;
        this.tickerSourceText = 'Treasury';
        return;
      }

      this.loadDealslipTickerData();
    }, () => this.loadDealslipTickerData());
  }

  loadDealslipTickerData(): void {
    this.apiService.getDealslip(1, '').subscribe(data => {
      this.checkService.checkLoggedin(data);
      const dealslipRates = data.success === true ? this.dealslipTickerItems(data.data || []) : [];
      this.tickerItems = dealslipRates;
      this.tickerSourceText = dealslipRates.length > 0 ? 'Dealslip' : 'Rates unavailable';
    }, () => {
      this.tickerItems = [];
      this.tickerSourceText = 'Rates unavailable';
    });
  }

  treasuryTickerItems(rows: any[]): TickerItem[] {
    return (rows || []).slice(0, 8).map(row => ({
      pair: String(row.pair || [row.source, row.target].filter(Boolean).join('/') || 'Rate'),
      value: this.formatTickerNumber(Number(row.finalRate || row.rate || 0)),
      change: row.lastUpdated ? 'Updated ' + this.shortTime(row.lastUpdated) : 'Treasury',
      down: false,
    })).filter(item => item.value !== '-');
  }

  dealslipTickerItems(rows: any[]): TickerItem[] {
    const seen = new Set<string>();
    const items: TickerItem[] = [];
    (rows || []).forEach(row => {
      const pair = [row.sellCurrency?.code, row.buyCurrency?.code].filter(Boolean).join('/');
      const rate = Number(row.rate || 0);
      if (!pair || !rate || seen.has(pair)) {
        return;
      }

      seen.add(pair);
      items.push({
        pair,
        value: this.formatTickerNumber(rate),
        change: row.date ? 'Updated ' + this.shortTime(row.date) : 'Dealslip',
        down: false,
      });
    });

    return items.slice(0, 8);
  }

  formatTickerNumber(value: number): string {
    if (!Number.isFinite(value) || value <= 0) {
      return '-';
    }

    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  shortTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value).slice(0, 16);
    }

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  totalNotifications(): number {
    return Number(this.userData?.notification?.total || 0) + Number(this.unreadAnnouncementCount || 0);
  }

  pageTitle(): string {
    const clean = this.router.url.split('?')[0].replace(/^\/+/, '');
    if (clean === '') return 'Dashboard';
    const first = clean.split('/')[0];
    const titles = {
      dealslip: 'Deal Slip',
      hr: 'People',
      treasury: 'Treasury Module',
      'finance-pl': 'Finance & P&L',
      users: 'Customers',
      settings: 'Settings',
      profile: 'Profile',
    };
    return titles[first] || first.replace(/-/g, ' ');
  }

  setLocation(location) {
    this.apiService.setLocation(location).subscribe(
      data => {
        this.checkService.checkLoggedin(data);
        if (data.success == true) {
          var currentTime = Math.floor((Date.now() / 1000) + (60 * 10));
          
          let randKey = this.checkService.getRandomString(32);
          var keydata = {
            key: randKey,
            expire: currentTime.toString()
          }

          localStorage.setItem('key', btoa( JSON.stringify(keydata)) );
          localStorage.setItem('userData', this.EncrDecr.set(environment.localKey+randKey, JSON.stringify(data.data)));
          this.userData = data.data;

          window.location.href = this.pageUrl;
        } else {
          this.notifyService.showError(data.error.message, "Error")
        }
      }
    );
  }

  addS(number: number, text: string) {
    const twoLetter = ["ss", "sh", "ch"];
    const oneLetter = ["s", "x", "z"];
    const vowelY = ["ay", "ey", "iy", "oy", "uy"];
    const lastTwo = text.slice(-2);
    const lastOne = text.slice(-1);
    if (number > 1){
      if (twoLetter.includes(lastTwo)) {
        text = text + "es";
      } else if (oneLetter.includes(lastOne)) {
        text = text + "es";
      } else if (vowelY.includes(lastOne)) {
        text = text + "s";
      } else if (lastOne.toLowerCase() == "y") {
        text = text.slice(0, -1) + "ies";
      } else {
        text = text + "s";
      }
    }

    return text;
  }
}
